import React from 'react';
import { AppShell, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { Header } from '../Header/Header';
import { StatusBar } from '../StatusBar';
import { Outlet } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { useTranslation } from 'react-i18next';
import { useClearAllContents } from '@hooks/useClearAllContents';
import { checkHealth } from '@store/features/health/healthSlice';
import styles from './MainLayout.module.scss';

interface MainLayoutProps {
  onThemeToggle: () => void;
  isDark: boolean;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ onThemeToggle, isDark }) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const isTablet = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const { clearAllContents } = useClearAllContents();

  // Usar el estado global de la aplicación
  const status = useAppSelector(state => state.app.status);
  const { status: healthStatus, version, isLoading } = useAppSelector(state => state.health);

  const handleRecheckHealth = () => {
    dispatch(checkHealth());
  };

  const handleClearAll = () => {
    modals.openConfirmModal({
      title: t('contentTable.confirmDeleteTitle'),
      centered: true,
      children: (
        <React.Fragment>
          <p>{t('contentTable.confirmClearAll')}</p>
          <p>{t('contentTable.confirmClearAllWarning')}</p>
        </React.Fragment>
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => clearAllContents(),
    });
  };

  return (
    <AppShell
      header={{ height: isMobile ? 50 : isTablet ? 55 : 60 }}
      footer={{ height: 30 }}
      padding={isMobile ? 'xs' : isTablet ? 'sm' : 'md'}
      className={styles.appShell}
    >
      <AppShell.Header className={styles.header}>
        <Header
          isMobile={isMobile}
          isTablet={isTablet}
          onThemeToggle={onThemeToggle}
          isDark={isDark}
          onClearAll={handleClearAll}
          status={healthStatus || ''}
          version={version || ''}
          isLoading={isLoading}
          onRecheckHealth={handleRecheckHealth}
        />
      </AppShell.Header>

      <AppShell.Main className={styles.main}>
        <Outlet />
      </AppShell.Main>

      <AppShell.Footer className={styles.footer}>
        {/* 
          Easter Egg: Haz clic 5 veces en la barra de estado para activar el modo desarrollador.
          Esta funcionalidad está oculta intencionalmente para uso de desarrolladores.
        */}
        <StatusBar status={status} />
      </AppShell.Footer>
    </AppShell>
  );
};
